import React, { useMemo, useState } from "react";
import { Box, Typography, Paper, CircularProgress, Alert, IconButton, Stack, Chip } from "@mui/material";
import { ChevronLeft, ChevronRight, CalendarToday } from "@mui/icons-material";
import { useGetAuthorityTimelineQuery } from "@/services/product-catalogue/duty-columns-api";
import { format } from "date-fns";

// New Figma color palette
const FIGMA_COLORS = [
  '#0A6FE9', '#C62CB0', '#14DCB2', '#0D9857', '#07C9F6', '#711C9A'
];

// Authority color mapping - using a combination of specific and cycled colors
const getAuthorityColor = (authority: string, index: number): string => {
  const specificColors: Record<string, string> = {
    SECTION_301: FIGMA_COLORS[1], // Pink/Magenta
    SECTION_232: FIGMA_COLORS[0], // Blue
    MFN: "#6c757d", // A neutral grey for standard rates
  };

  if (specificColors[authority]) {
    return specificColors[authority];
  }
  
  // Cycle through the remaining Figma colors for other authorities
  const remainingColors = FIGMA_COLORS.slice(2);
  return remainingColors[index % remainingColors.length];
};

interface TimelinePeriod {
  startDate: string;
  endDate: string;
  activeColumns: {
    name: string;
    priority: number;
    effectiveRate: number;
    ruleId: string;
    ruleNumber: number;
  }[];
}

interface HoveredPeriod {
  authority: string;
  period: TimelinePeriod;
  x: number;
  y: number;
}

const AuthorityTimeline: React.FC = () => {
  const { data, isLoading, error } = useGetAuthorityTimelineQuery();
  const [hoveredPeriod, setHoveredPeriod] = useState<HoveredPeriod | null>(null);
  const [isPopoverHovered, setIsPopoverHovered] = useState(false);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  // Date range state - default to last year through next year
  const today = new Date();
  const [dateRange, setDateRange] = useState({
    start: new Date(today.getFullYear() - 1, 0, 1),
    end: new Date(today.getFullYear() + 1, 11, 31),
  });

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Calculate timeline bounds and demarcation dates
  const timelineData = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Collect all unique dates
    const allDates = new Set<string>();
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    data.forEach((authorityData) => {
      authorityData.periods.forEach((period) => {
        // Dates are ISO strings from API; parse safely with Date constructor
        const startDateParsed = new Date(period.startDate);
        const endDateParsed = new Date(period.endDate);
          
          if (isNaN(startDateParsed.getTime()) || isNaN(endDateParsed.getTime())) {
            console.error('Invalid date format:', { startDate: period.startDate, endDate: period.endDate });
            return;
          }

          allDates.add(period.startDate);
          allDates.add(period.endDate);

          if (!minDate || startDateParsed < minDate) minDate = startDateParsed;
          if (!maxDate || endDateParsed > maxDate) maxDate = endDateParsed;
      });
    });

    if (!minDate || !maxDate) {
      console.error('No valid dates found in timeline data');
      return null;
    }

    // Use the date range from state
    const timelineMinDate = dateRange.start;
    const timelineMaxDate = dateRange.end;

    // Filter demarcation dates to only include those within our timeline range
    const filteredDates = Array.from(allDates)
      .filter(dateStr => {
        const date = new Date(dateStr);
        return date >= timelineMinDate && date <= timelineMaxDate;
      })
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    return {
      minDate: timelineMinDate,
      maxDate: timelineMaxDate,
      demarcationDates: filteredDates,
      todayDate: new Date(),
      dataMinDate: minDate, // Keep track of actual data bounds
      dataMaxDate: maxDate,
    };
  }, [data, dateRange]);

  const handlePeriodHover = (
    event: React.MouseEvent,
    authority: string,
    period: TimelinePeriod
  ) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredPeriod({
      authority,
      period,
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const handlePeriodLeave = () => {
    // Add a small delay before hiding to allow moving to popover
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isPopoverHovered) {
        setHoveredPeriod(null);
      }
    }, 100);
  };

  const handlePopoverEnter = () => {
    setIsPopoverHovered(true);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handlePopoverLeave = () => {
    setIsPopoverHovered(false);
    setHoveredPeriod(null);
  };

  // Date range navigation functions
  const shiftDateRange = (months: number) => {
    setDateRange(prev => ({
      start: new Date(prev.start.getFullYear(), prev.start.getMonth() + months, 1),
      end: new Date(prev.end.getFullYear(), prev.end.getMonth() + months + 1, 0),
    }));
  };

  const resetToDefault = () => {
    const today = new Date();
    setDateRange({
      start: new Date(today.getFullYear() - 1, 0, 1),
      end: new Date(today.getFullYear() + 1, 11, 31),
    });
  };

  // Calculate position on timeline
  const getTimelinePosition = (date: string | number | Date, minDate: Date, maxDate: Date): number => {
    let dateObj: Date;
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'number') {
      dateObj = new Date(date);
    } else {
      const numeric = Number(date);
      dateObj = isNaN(numeric) ? new Date(date) : new Date(numeric);
    }
    const totalDuration = maxDate.getTime() - minDate.getTime();
    const dateDuration = dateObj.getTime() - minDate.getTime();
    return (dateDuration / totalDuration) * 100;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load authority timeline data. Please try again later.
      </Alert>
    );
  }

  if (!data || data.length === 0 || !timelineData) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No authority timeline data available.
      </Alert>
    );
  }

  const { minDate, maxDate, demarcationDates, todayDate } = timelineData;
  const todayPosition = getTimelinePosition(todayDate, minDate!, maxDate!);

  return (
    <Paper sx={{ p: 3, mt: 2, backgroundColor: '#FAFAFA' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight="bold">
          Authority Timeline
        </Typography>
        
        {/* Date range controls */}
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton 
            size="small" 
            onClick={() => shiftDateRange(-12)}
            sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#F5F5F5' } }}
          >
            <ChevronLeft />
          </IconButton>
          
          <Chip
            icon={<CalendarToday sx={{ fontSize: 16 }} />}
            label={`${format(dateRange.start, "MMM yyyy")} - ${format(dateRange.end, "MMM yyyy")}`}
            onClick={resetToDefault}
            sx={{ 
              bgcolor: 'white',
              '&:hover': { bgcolor: '#F5F5F5' },
              cursor: 'pointer',
            }}
          />
          
          <IconButton 
            size="small" 
            onClick={() => shiftDateRange(12)}
            sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#F5F5F5' } }}
          >
            <ChevronRight />
          </IconButton>
        </Stack>
      </Stack>
      
      <Box sx={{ position: "relative", mt: 4, pl: 15, pr: 2 }}>
        {/* Timeline container */}
        <Box sx={{ position: "relative", minHeight: data.length * 70 + 100, bgcolor: 'white', borderRadius: 2, p: 2 }}>
          {/* Demarcation lines */}
          {demarcationDates.map((date) => {
            const position = getTimelinePosition(date, minDate!, maxDate!);
            return (
              <Box
                key={date}
                sx={{
                  position: "absolute",
                  left: `${position}%`,
                  top: 0,
                  bottom: 40,
                  width: 2,
                  backgroundColor: "#E3E3E3",
                  zIndex: 1,
                  opacity: 0.6,
                }}
              />
            );
          })}

          {/* Today marker */}
          <Box
            sx={{
              position: "absolute",
              left: `${todayPosition}%`,
              top: -10,
              bottom: 30,
              width: 3,
              backgroundColor: "#0B494B",
              zIndex: 5,
              boxShadow: '0 0 8px rgba(11, 73, 75, 0.3)',
            }}
          />
          <Chip
            label="Today"
            size="small"
            sx={{
              position: "absolute",
              left: `${todayPosition}%`,
              bottom: 5,
              transform: "translateX(-50%)",
              backgroundColor: "#0B494B",
              color: "white",
              fontWeight: "bold",
              fontSize: '12px',
              height: 22,
            }}
          />

          {/* Authority lanes */}
          {data.map((authorityData, index) => (
            <Box
              key={authorityData.authority}
              sx={{
                position: "absolute",
                top: index * 70,
                left: 0,
                right: 0,
                height: 60,
                borderBottom: index < data.length - 1 ? '1px solid #F0F0F0' : 'none',
              }}
            >
              {/* Authority label */}
              <Chip
                label={authorityData.authority.replace(/_/g, ' ')}
                sx={{
                  position: "absolute",
                  left: -140,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: getAuthorityColor(authorityData.authority, index),
                  color: "white",
                  fontWeight: "bold",
                  fontSize: '12px',
                }}
              />

              {/* Period bars */}
              {authorityData.periods.map((period, periodIndex) => {
                const periodStart = new Date(period.startDate);
                const periodEnd = new Date(period.endDate);
                
                // Skip periods that are completely outside our timeline range
                if (periodEnd < minDate! || periodStart > maxDate!) {
                  return null;
                }
                
                // Clamp the period to our timeline range
                const clampedStart = periodStart < minDate! ? minDate! : periodStart;
                const clampedEnd = periodEnd > maxDate! ? maxDate! : periodEnd;
                
                const startPos = getTimelinePosition(clampedStart, minDate!, maxDate!);
                const endPos = getTimelinePosition(clampedEnd, minDate!, maxDate!);
                const width = endPos - startPos;

                return (
                  <Box
                    key={`${authorityData.authority}-${periodIndex}`}
                    sx={{
                      position: "absolute",
                      left: `${startPos}%`,
                      width: `${width}%`,
                      height: 45,
                      backgroundColor: getAuthorityColor(authorityData.authority, index),
                      borderRadius: 1,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      "&:hover": {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        zIndex: 10,
                      },
                      zIndex: 3,
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: 1,
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => handlePeriodHover(e, authorityData.authority, period)}
                    onMouseLeave={handlePeriodLeave}
                  >
                    {/* Show active rules count if bar is wide enough */}
                    {width > 5 && (
                      <Box
                        component="span"
                        sx={{ 
                          color: 'white', 
                          fontWeight: 'bold',
                          fontSize: '11px',
                          opacity: 0.9,
                        }}
                      >
                        {period.activeColumns.length} rules
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}

          {/* Date labels - show quarterly labels */}
          <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0 }}>
            {(() => {
              const labels = [];
              const startYear = minDate!.getFullYear();
              const endYear = maxDate!.getFullYear();
              
              for (let year = startYear; year <= endYear; year++) {
                for (let month = 0; month < 12; month += 3) { // Quarterly
                  const date = new Date(year, month, 1);
                  if (date >= minDate! && date <= maxDate!) {
                    const position = getTimelinePosition(date, minDate!, maxDate!);
                    labels.push(
                      <Typography
                        key={`${year}-${month}`}
                        variant="caption"
                        sx={{
                          position: "absolute",
                          left: `${position}%`,
                          transform: "translateX(-50%)",
                          color: "#666",
                        }}
                      >
                        {format(date, "MMM yyyy")}
                      </Typography>
                    );
                  }
                }
              }
              
              return labels;
            })()}
          </Box>
        </Box>

        {/* Hover popover */}
        {hoveredPeriod && (
          <Paper
            elevation={8}
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handlePopoverLeave}
            sx={{
              position: "fixed",
              left: hoveredPeriod.x,
              top: hoveredPeriod.y + 60,
              transform: "translateX(-50%)",
              p: 2.5,
              zIndex: 1000,
              minWidth: 320,
              maxWidth: 400,
              maxHeight: '80vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid',
              borderColor: getAuthorityColor(hoveredPeriod.authority, 0),
              borderRadius: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Chip
                label={hoveredPeriod.authority.replace(/_/g, ' ')}
                size="small"
                sx={{
                  backgroundColor: getAuthorityColor(hoveredPeriod.authority, 0),
                  color: "white",
                  fontWeight: "bold",
                }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Period: {(() => {
                try {
                  const start = new Date(hoveredPeriod.period.startDate);
                  const end = new Date(hoveredPeriod.period.endDate);
                  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                    return `${hoveredPeriod.period.startDate} – ${hoveredPeriod.period.endDate}`;
                  }
                  return `${format(start, "MMM d, yyyy")} – ${format(end, "MMM d, yyyy")}`;
                } catch {
                  return `${hoveredPeriod.period.startDate} – ${hoveredPeriod.period.endDate}`;
                }
              })()}
            </Typography>
            
            {hoveredPeriod.period.activeColumns.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold" color="text.primary" gutterBottom>
                  Active Duty Rules ({hoveredPeriod.period.activeColumns.length}):
                </Typography>
                <Box 
                  sx={{ 
                    maxHeight: 300, 
                    overflowY: 'auto',
                    pr: 1,
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#888',
                      borderRadius: '3px',
                      '&:hover': {
                        backgroundColor: '#555',
                      },
                    },
                  }}
                >
                  <Stack spacing={0.5}>
                    {(() => {
                      // Sort the current period's rules by priority
                      const sortedRules = [...hoveredPeriod.period.activeColumns]
                        .sort((a, b) => a.priority - b.priority);
                      
                      return sortedRules.map((rule) => {
                          
                          // Check if rule is expiring
                          const authorityData = data?.find(a => a.authority === hoveredPeriod.authority);
                          const currentPeriodIndex = authorityData?.periods.findIndex(p => 
                            p.startDate === hoveredPeriod.period.startDate && 
                            p.endDate === hoveredPeriod.period.endDate
                          ) ?? -1;
                          
                          let isExpiring = false;
                          if (authorityData && currentPeriodIndex !== -1 && currentPeriodIndex < authorityData.periods.length - 1) {
                            const nextPeriod = authorityData.periods[currentPeriodIndex + 1];
                            
                            // Check if this specific rule exists in the next period
                            const ruleExistsInNextPeriod = nextPeriod.activeColumns.some((r: typeof rule) => 
                              r.ruleId === rule.ruleId
                            );
                            
                            // If the rule doesn't exist in the next period, it's expiring
                            if (!ruleExistsInNextPeriod) {
                              isExpiring = true;
                            }
                          } else if (authorityData && currentPeriodIndex === authorityData.periods.length - 1) {
                            // This is the last period, so all rules expire at the end
                            isExpiring = true;
                          }
                          
                          const periodEnd = new Date(hoveredPeriod.period.endDate);
                          const isWithinDateRange = periodEnd >= dateRange.start && periodEnd <= dateRange.end;
                          const isExpiringInRange = isExpiring && isWithinDateRange;
                          
                          return (
                          <Box 
                            key={rule.ruleId} 
                            sx={{ 
                              pl: 2, 
                              py: 1,
                              borderLeft: '3px solid',
                              borderLeftColor: isExpiringInRange ? '#FF6B6B' : getAuthorityColor(hoveredPeriod.authority, 0),
                              backgroundColor: isExpiringInRange ? '#FFF3F3' : '#F5F5F5',
                              borderRadius: '0 4px 4px 0',
                              position: 'relative',
                            }}
                          >
                            <Stack direction="row" alignItems="flex-start" spacing={1}>
                              <Chip 
                                label={`Rule ${rule.ruleNumber}`} 
                                size="small"
                                sx={{ 
                                  minWidth: 60,
                                  height: 20,
                                  fontSize: '11px',
                                  bgcolor: isExpiringInRange ? '#FF6B6B' : getAuthorityColor(hoveredPeriod.authority, 0),
                                  color: 'white',
                                }}
                              />
                              <Box flex={1}>
                                <Typography variant="body2" fontSize="13px" fontWeight="bold">
                                  {rule.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="div">
                                  Priority: {rule.priority} • Rate: {(rule.effectiveRate * 100).toFixed(1)}%
                                </Typography>
                                {isExpiringInRange && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: '#FF6B6B',
                                      fontWeight: 'bold',
                                      display: 'block',
                                      mt: 0.5,
                                    }}
                                  >
                                    ⚠️ Expires: {format(periodEnd, "MMM d, yyyy")}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </Box>
                        );
                      });
                    })()}
                  </Stack>
                </Box>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Paper>
  );
};

export default AuthorityTimeline;